// eslint not recognizing `RequestInit` even though it's part of `lib.dom` in tsconfig
/* global RequestInit */
import { getCSRFToken } from "@/utils/authentication";

interface FetcherOptions {
  createBody?: (payload: { arg: any }) => string;
  requestOptions?: RequestInit;
  transformUrl?: (url: string, payload: { arg: any }) => string;
}

export const MUTATION_OPTIONS = Object.freeze({
  populateCache: true, // See https://github.com/vercel/swr/issues/2267
  revalidate: false,
  throwOnError: false, // See https://github.com/vercel/swr/issues/1973
} as const);

const swrFetcher = <T>(
  error: string = "Something went wrong.",
  { createBody, requestOptions, transformUrl }: FetcherOptions = {},
) => {
  return async function (url: string, payload?: { arg: any }): Promise<T> {
    const resource = transformUrl && payload ? transformUrl(url, payload) : url;
    const body = createBody && payload ? createBody(payload) : null;
    const res = await window.fetch(resource, {
      headers: { Accept: "application/json" },
      method: "GET",
      body,
      ...requestOptions,
    });
    const data = await res
      .json()
      .catch(() => ({ errors: [`${error} (JSON parse error)`, url] }));

    if (!res.ok) {
      throw new Error(error, {
        cause: { key: data.errors[0], values: data.errors },
      });
    }

    return data;
  };
};

export const getJSON = <T>(error: string, options: FetcherOptions = {}) =>
  swrFetcher<T>(error, options);

export const getJSONWithCredentials = <T>(
  error: string,
  options: FetcherOptions = {},
) =>
  swrFetcher<T>(error, {
    requestOptions: {
      credentials: "include",
      ...options.requestOptions,
    },
    ...options,
  });

export const postJSON = <T>(
  error: string,
  { createBody, requestOptions, transformUrl }: FetcherOptions = {},
) =>
  swrFetcher<T>(error, {
    createBody,
    requestOptions: {
      method: "POST",
      ...requestOptions,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
    transformUrl,
  });

export const postJSONWithCSRF = <T>(
  error: string,
  { createBody, requestOptions, transformUrl }: FetcherOptions = {},
) =>
  swrFetcher<T>(error, {
    createBody,
    requestOptions: {
      method: "POST",
      credentials: "include",
      ...requestOptions,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken() ?? "",
        ...requestOptions?.headers,
      },
    },
    transformUrl,
  });
